import type { SignalVariantTypes } from '@etg/wings';
import { Badge, breakpoints, Card, Inline, signalVariants, Stack, Text, vars } from '@etg/wings';
import { CarrierLogo } from '@eti/components';
import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import type { FlightInformationType } from '../../../models';
import RouteInfo from '../../common/RouteInfo';

const cardStyles = css`
  border: 1px solid ${vars.colors.divider};
`;

const carrierLogoStyles = css`
  height: 24px;
  width: 24px;
`;

const titleBadgeInlineStyles = css`
  align-items: start;
  flex-flow: wrap-reverse;
  flex-direction: column-reverse;
  gap: 8px 16px;
  justify-content: space-between;

  @media (min-width: ${breakpoints._560}) {
    flex-direction: row;
  }
`;

const dividerStyles = css`
  background-color: ${vars.colors.divider};
  height: 1px;
  width: 100%;
`;

interface CancellationPnrCardProps {
  providerBooking: FlightInformationType[number];
}

const CancellationPnrCard = ({ providerBooking }: CancellationPnrCardProps) => {
  const { t } = useTranslation();

  const pnrState: {
    badge: { variant?: SignalVariantTypes; text: string };
    description: string;
  } = providerBooking.isEligibleForScheduleChangeRefund
    ? {
        badge: {
          variant: signalVariants.POSITIVE,
          text: t('ManageTrip.Refundable.Badge.Title'),
        },
        description: t('ManageTrip.RefundRequest.Description'),
      }
    : {
        badge: { text: t('ManageTrip.Unknown.Badge.Title') },
        description: t('ManageTrip.RefundRequest.Unknown.Description'),
      };

  return (
    <Card className={cardStyles} data-testid="pnr-refundability-card">
      <Stack>
        <Inline className={titleBadgeInlineStyles}>
          <Inline alignY="top" noWrap spacing={8}>
            <CarrierLogo
              className={carrierLogoStyles}
              data-testid={`pnr-refundability-card-carrier-logo-${providerBooking.pnr}`}
              id={providerBooking.carrier.code || ''}
              imageAlt={providerBooking.carrier.name || ''}
            />
            <Stack data-testid="pnr-refundability-segments">
              {providerBooking.segments.map((segment, index) => (
                <RouteInfo
                  dataTestId={`pnr-refundability-segment-${index}`}
                  key={index}
                  route={{
                    destination: {
                      cityName: segment.destination,
                      cityCode: segment.destinationCityCode,
                    },
                    origin: {
                      cityName: segment.origin,
                      cityCode: segment.originCityCode,
                    },
                    arrivedAt: segment.arrivedAt,
                    departuredAt: segment.departuredAt,
                  }}
                />
              ))}
            </Stack>
          </Inline>
          <Badge
            data-testid={`pnr-refundability-card-status-${providerBooking.pnr}`}
            variant={pnrState.badge.variant}
          >
            {pnrState.badge.text}
          </Badge>
        </Inline>
        <span className={dividerStyles} />
        <Text>{pnrState.description}</Text>
      </Stack>
    </Card>
  );
};

export default CancellationPnrCard;
